import { faroCoreApiClient } from "@api/faro-core-api-client";
import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import {
  BaseCompanyIdProps,
  BaseProjectIdProps,
} from "@custom-types/base-types";
import { BaseMemberProps } from "@custom-types/member-types";
import { useToast } from "@hooks/use-toast";
import { Link } from "@mui/material";
import { blue } from "@flat-ui";
import {
  ProjectMemberEvents,
  TableType,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SHOW_ON_HOVER_CLASS } from "@utils/ui-utils";
import { isMemberActive } from "@utils/user-utils";

interface Props
  extends BaseCompanyIdProps,
    Partial<BaseProjectIdProps>,
    BaseMemberProps {
  /** Recognizes on which subject this component is being used */
  subjectType: MemberTableSubject;
}

/**
 * Resend invite for a single unregistered member of:
 * projects when project is available and component rendered under the team tab of project overview,
 * workspace when project is not available because component is rendered under member on the workspace level.
 * This component is hidden by default and it is shown on table hover using ${SHOW_ON_HOVER_CLASS}.
 */
export function ResendInvitation({
  companyId,
  projectId,
  member,
  subjectType,
}: Props): JSX.Element | null {
  const { handleErrorWithToast } = useErrorContext();
  const { showToast } = useToast();
  const { trackEvent } = useTrackEvent();

  async function onResendInvitationClick(): Promise<void> {
    trackEvent({
      name: ProjectMemberEvents.resendInvitation,
      props: {
        dataType:
          subjectType === "project"
            ? TableType.projectMembers
            : TableType.companyMembers,
      },
    });

    try {
      // Resend invite to project
      if (projectId && subjectType === "project") {
        // No need to be an asyncThunk as we don't need to update the store
        await faroCoreApiClient.projects.resendProjectMembershipInvite({
          companyId,
          projectId,
          email: member.email,
        });
      }

      // Resend invite to workspace or group
      if (
        !projectId &&
        (subjectType === "workspace" || subjectType === "group")
      ) {
        // No need to be an asyncThunk as we don't need to update the store
        await faroCoreApiClient.company.resendCompanyMembershipInvite({
          companyId,
          userIdentity: member.email,
        });
      }

      showToast({
        message: "Invitation sent",
        type: "success",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `resendInvite-${Date.now().toString()}`,
        title: "Could not send invitation",
        error,
      });
    }
  }

  if (isMemberActive(member)) {
    return null;
  }

  return (
    <Link
      onClick={onResendInvitationClick}
      underline="always"
      className={SHOW_ON_HOVER_CLASS}
      sx={{
        marginLeft: "10px",
        color: blue[500],
        textDecorationColor: blue[500],
        cursor: "pointer",
        // This will be overridden by parent component using SHOW_ON_HOVER_CLASS
        visibility: "hidden",
      }}
    >
      Resend Invite
    </Link>
  );
}
